/**
 * Bilt Passport — web API wrappers.
 *
 * Additive slice mirroring docs/BILT_PASSPORT_API_ARCHITECTURE.md and
 * docs/BILT_PASSPORT_DATA_MODEL.md. All server flags default OFF
 * (`passport_enabled` is the master switch); every function here is safe to
 * call even before the backend endpoints ship — failures surface as normal
 * thrown errors so callers can render the honest disabled/empty state
 * instead of a broken UI.
 *
 * Product tone: a scan is proof of presence ("I was actually here"), not a
 * score. Never introduce XP/points/leaderboard language around these types.
 */
import apiService from '../ApiService';

export type PassportEncounterStatus = 'pending' | 'verified' | 'rejected' | 'revoked';

export type PassportVerificationLevel =
  | 'basic_qr'
  | 'time_verified'
  | 'event_verified'
  | 'proximity_verified'
  | 'staff_verified'
  | 'offline_pending'
  | 'rejected_suspicious';

export type PassportRiskStatus = 'clear' | 'flagged' | 'held';

export type PassportSubjectType =
  | 'vehicle'
  | 'event'
  | 'shop'
  | 'club'
  | 'booth'
  | 'activation'
  | 'destination'
  | 'checkpoint';

export type PassportEncounterMethod =
  | 'qr_scan'
  | 'nfc_tap'
  | 'manual_code'
  | 'geofence_checkin'
  | 'organizer_grant'
  | 'system_grant';

export interface PassportFeatureFlags {
  passport_enabled: boolean;
  vehicle_encounters_enabled: boolean;
  event_encounters_enabled: boolean;
  passport_collections_enabled: boolean;
  shop_encounters_enabled: boolean;
  club_encounters_enabled: boolean;
  sponsor_campaigns_enabled: boolean;
  passport_rewards_enabled: boolean;
  passport_location_verification_enabled: boolean;
  passport_offline_scans_enabled: boolean;
  passport_public_profiles_enabled: boolean;
}

const DISABLED_FLAGS: PassportFeatureFlags = {
  passport_enabled: false,
  vehicle_encounters_enabled: false,
  event_encounters_enabled: false,
  passport_collections_enabled: false,
  shop_encounters_enabled: false,
  club_encounters_enabled: false,
  sponsor_campaigns_enabled: false,
  passport_rewards_enabled: false,
  passport_location_verification_enabled: false,
  passport_offline_scans_enabled: false,
  passport_public_profiles_enabled: false,
};

export interface PassportSubjectPreview {
  subject_type: PassportSubjectType;
  subject_id: number;
  title: string;
  subtitle?: string | null;
  thumbnail_url?: string | null;
  public_slug?: string | null;
  owner_username?: string | null;
}

export interface PassportEncounter {
  id: number;
  public_id: string;
  vehicle_id: number | null;
  subject_type: PassportSubjectType;
  subject_id: number;
  subject?: PassportSubjectPreview;
  campaign_id: number | null;
  method: PassportEncounterMethod;
  occurred_at: string;
  recorded_at: string;
  status: PassportEncounterStatus;
  verification_level: PassportVerificationLevel;
  risk_status: PassportRiskStatus;
  memory_caption?: string | null;
  memory_visibility?: 'private' | 'public' | null;
  media_count?: number;
}

export interface PassportEncounterDetail extends PassportEncounter {
  media?: Array<{ id: number; media_type: 'image' | 'video'; storage_path: string; thumbnail_path?: string | null }>;
}

export interface PassportSummary {
  encounter_count: number;
  verified_encounter_count: number;
  pending_encounter_count: number;
  vehicles_encountered_count: number;
  events_encountered_count: number;
  collections_in_progress: number;
  collections_completed: number;
  campaigns_joined: number;
  campaigns_completed: number;
  recent_encounters: PassportEncounter[];
}

export interface VehiclePassportStats {
  vehicle_id?: number;
  public_count_enabled: boolean;
  seen_in_person_count: number;
  qr_encounter_count?: number;
}

export interface PassportCollectionRequirement {
  requirement_key: string | null;
  title?: string | null;
  description?: string | null;
  hidden: boolean;
  satisfied?: boolean;
}

export interface PassportCollectionDefinition {
  id: number;
  public_id: string;
  slug: string;
  collection_type: string;
  title: string;
  subtitle?: string | null;
  description?: string | null;
  visibility: 'public' | 'hidden' | 'limited';
  owner_type: string;
  requirements?: PassportCollectionRequirement[];
  progress?: { percent_complete: number; status: string } | null;
}

export interface PassportCampaignObject {
  public_id: string;
  object_kind: 'booth' | 'activation' | 'destination' | 'checkpoint';
  title: string;
  description?: string | null;
  is_required: boolean;
  is_hidden: boolean;
}

export interface PassportCampaignSummary {
  id: number;
  public_id: string;
  slug: string;
  campaign_type: string;
  owner_type: string;
  sponsor_name?: string | null;
  title: string;
  description?: string | null;
  hero_media_path?: string | null;
  status: 'draft' | 'scheduled' | 'active' | 'paused' | 'ended' | 'archived';
  starts_at?: string | null;
  ends_at?: string | null;
  is_public: boolean;
  required_objects_total?: number;
  objects?: PassportCampaignObject[];
  participation?: {
    status: string;
    objects_visited_count: number;
    required_objects_total: number;
  } | null;
}

export interface PassportScanResult {
  /** Null only for the `owner_self` case — you can't log an encounter with your own build. */
  encounter_id: number | null;
  public_id: string | null;
  status: PassportEncounterStatus | null;
  verification_level?: PassportVerificationLevel | null;
  subject: PassportSubjectPreview | null;
  dedupe_outcome: 'new' | 'already_recorded';
  owner_self?: boolean;
}

/**
 * Admin encounter row shape from `passportAdminListEncounters()` — same fields as
 * `PassportEncounter` (no embedded subject/scanner preview or risk reasons yet;
 * the admin facade returns bare encounter rows).
 */
export type PassportReviewQueueItem = PassportEncounter;

export interface PassportScanAppeal {
  public_id: string;
  encounter_id: number;
  appeal_reason: string;
  status: 'open' | 'under_review' | 'upheld_rejection' | 'overturned_approved' | 'withdrawn';
  created_at: string;
  resolved_at?: string | null;
  resolution_notes?: string | null;
}

/**
 * Reads the flattened `feature_flags` map from `client_config.php` (safe,
 * no-auth bootstrap endpoint) and extracts the Passport slice, namespaced
 * `passport.<key>` per the existing `config/features.php` grouping
 * convention. Missing keys are treated as disabled — Passport is
 * fail-closed, not fail-open, since it is an unlaunched system.
 */
export async function getPassportFlags(): Promise<PassportFeatureFlags> {
  try {
    const response = await apiService.makeRequest<{ feature_flags?: Record<string, boolean> }>(
      '/client_config.php',
      { method: 'GET' }
    );
    const flat = response.data?.feature_flags || {};
    return {
      passport_enabled: Boolean(flat['passport.passport_enabled']),
      vehicle_encounters_enabled: Boolean(flat['passport.vehicle_encounters_enabled']),
      event_encounters_enabled: Boolean(flat['passport.event_encounters_enabled']),
      passport_collections_enabled: Boolean(flat['passport.passport_collections_enabled']),
      shop_encounters_enabled: Boolean(flat['passport.shop_encounters_enabled']),
      club_encounters_enabled: Boolean(flat['passport.club_encounters_enabled']),
      sponsor_campaigns_enabled: Boolean(flat['passport.sponsor_campaigns_enabled']),
      passport_rewards_enabled: Boolean(flat['passport.passport_rewards_enabled']),
      passport_location_verification_enabled: Boolean(
        flat['passport.passport_location_verification_enabled']
      ),
      passport_offline_scans_enabled: Boolean(flat['passport.passport_offline_scans_enabled']),
      passport_public_profiles_enabled: Boolean(flat['passport.passport_public_profiles_enabled']),
    };
  } catch {
    return { ...DISABLED_FLAGS };
  }
}

export function isPassportEnabled(flags: PassportFeatureFlags | null | undefined): boolean {
  return Boolean(flags?.passport_enabled);
}

/** Raw shape returned by `passportSummary()` in `backend/includes/passport_utils.php`. */
interface PassportSummaryApiShape {
  total_encounters: number;
  encounters_by_subject_type: Partial<Record<PassportSubjectType, number>>;
  collections_in_progress: number;
  collections_completed: number;
  campaigns_joined: number;
  campaigns_completed: number;
  recent_encounters: PassportEncounter[];
}

export async function getPassportSummary(): Promise<PassportSummary> {
  const response = await apiService.makeRequest<PassportSummaryApiShape>('/passport_summary.php', {
    method: 'GET',
    headers: apiService.getAuthHeaders(),
  });
  if (!response.success || !response.data) {
    throw new Error(response.message || response.error || 'Failed to load Passport summary');
  }
  const raw = response.data;
  const bySubject = raw.encounters_by_subject_type || {};
  return {
    // Backend currently only counts verified encounters here; there is no
    // separate all-statuses total, so we report the verified count for both.
    encounter_count: raw.total_encounters,
    verified_encounter_count: raw.total_encounters,
    pending_encounter_count: 0,
    vehicles_encountered_count: bySubject.vehicle || 0,
    events_encountered_count: bySubject.event || 0,
    collections_in_progress: raw.collections_in_progress,
    collections_completed: raw.collections_completed,
    campaigns_joined: raw.campaigns_joined,
    campaigns_completed: raw.campaigns_completed,
    recent_encounters: raw.recent_encounters || [],
  };
}

export interface PassportEncounterFilters {
  subject_type?: PassportSubjectType;
  vehicle_id?: number;
  status?: PassportEncounterStatus;
  date_from?: string;
  date_to?: string;
  page?: number;
  limit?: number;
}

export async function listPassportEncounters(
  filters: PassportEncounterFilters = {}
): Promise<{ data: PassportEncounter[]; total_pages: number; page: number }> {
  const { page, limit, ...rest } = filters;
  const pageNum = page && page > 0 ? page : 1;
  const limitNum = limit && limit > 0 ? limit : 20;
  const qs = new URLSearchParams();
  Object.entries(rest).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    qs.set(key, String(value));
  });
  qs.set('limit', String(limitNum));
  qs.set('offset', String((pageNum - 1) * limitNum));
  const response = await apiService.makeRequest<PassportEncounter[]>(
    `/passport_encounters.php?${qs}`,
    { method: 'GET', headers: apiService.getAuthHeaders() }
  );
  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to load your Passport timeline');
  }
  const pagination = (response as unknown as { pagination?: { total?: number; limit?: number; offset?: number } })
    .pagination;
  const total = pagination?.total ?? 0;
  const effectiveLimit = pagination?.limit ?? limitNum;
  return {
    data: Array.isArray(response.data) ? response.data : [],
    total_pages: effectiveLimit > 0 ? Math.max(1, Math.ceil(total / effectiveLimit)) : 1,
    page: pageNum,
  };
}

/** Raw shape returned by `passportEncounterDetail()` — memory is nested, not flattened. */
interface PassportEncounterDetailApiShape extends Omit<PassportEncounter, 'memory_caption' | 'memory_visibility'> {
  memory: { caption: string | null; visibility: 'private' | 'public'; updated_at: string } | null;
  media?: PassportEncounterDetail['media'];
  subject?: PassportSubjectPreview & { username?: string | null; slug?: string | null };
}

export async function getPassportEncounterDetail(publicId: string): Promise<PassportEncounterDetail> {
  const response = await apiService.makeRequest<PassportEncounterDetailApiShape>(
    `/passport_encounter_detail.php?public_id=${encodeURIComponent(publicId)}`,
    { method: 'GET', headers: apiService.getAuthHeaders() }
  );
  if (!response.success || !response.data) {
    throw new Error(response.message || response.error || 'Encounter not found');
  }
  const raw = response.data;
  const { memory, subject, ...rest } = raw;
  return {
    ...rest,
    memory_caption: memory?.caption ?? null,
    memory_visibility: memory?.visibility ?? null,
    subject: subject
      ? { ...subject, public_slug: subject.public_slug ?? subject.slug, owner_username: subject.owner_username ?? subject.username }
      : undefined,
  };
}

export async function savePassportEncounterMemory(
  publicId: string,
  payload: { caption?: string; visibility?: 'private' | 'public' }
): Promise<void> {
  const response = await apiService.makeRequest('/passport_encounter_memory.php', {
    method: 'POST',
    body: JSON.stringify({ encounter_public_id: publicId, ...payload }),
    headers: apiService.getAuthHeaders(),
  });
  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to save memory');
  }
}

export async function submitPassportScanAppeal(publicId: string, appealReason: string): Promise<void> {
  const response = await apiService.makeRequest('/passport_scan_appeal.php', {
    method: 'POST',
    body: JSON.stringify({ encounter_public_id: publicId, appeal_reason: appealReason }),
    headers: apiService.getAuthHeaders(),
  });
  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to submit appeal');
  }
}

export interface PassportScanSubmission {
  payload: string;
  method: 'manual_code' | 'qr_scan';
  vehicle_id?: number;
  client_scan_id?: string;
}

/** Raw shape from `passport_scan.php` — encounter/subject are nested, and `duplicate` is a boolean. */
interface PassportScanApiShape {
  encounter: PassportEncounter | null;
  duplicate?: boolean;
  owner_self?: boolean;
  subject?: PassportSubjectPreview | null;
}

function passportScanResultFromRaw(raw: PassportScanApiShape): PassportScanResult {
  return {
    encounter_id: raw.encounter?.id ?? null,
    public_id: raw.encounter?.public_id ?? null,
    status: raw.encounter?.status ?? null,
    verification_level: raw.encounter?.verification_level ?? null,
    subject: raw.subject ?? null,
    dedupe_outcome: raw.duplicate ? 'already_recorded' : 'new',
    owner_self: Boolean(raw.owner_self),
  };
}

/** Web manual code / paste URL scan — a secondary path, not the primary mobile camera flow. */
export async function submitPassportScan(payload: PassportScanSubmission): Promise<PassportScanResult> {
  try {
    const response = await apiService.makeRequest<PassportScanApiShape>('/passport_scan.php', {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: apiService.getAuthHeaders(),
    });
    if (!response.success || !response.data) {
      throw new Error(response.message || response.error || 'Could not log that encounter');
    }
    return passportScanResultFromRaw(response.data);
  } catch (err: unknown) {
    // `dedupe_conflict` (HTTP 409) still identifies the existing encounter, but the
    // client throws on non-2xx responses rather than returning it — surface it as a
    // soft duplicate rather than a hard failure so the UI doesn't scare the user.
    const code = (err as { code?: string } | null)?.code;
    if (code === 'dedupe_conflict') {
      const dupeError = new Error("You've already logged this one.");
      (dupeError as Error & { code?: string }).code = code;
      throw dupeError;
    }
    throw err;
  }
}

/** Raw shape from `passportVehiclePublicCounts()` — "strong" is event/proximity/staff verified only. */
interface VehiclePassportStatsApiShape {
  public_count_enabled: boolean;
  seen_in_person_strong?: number;
  qr_encounters_basic?: number;
}

/** Public, opt-in aggregate for a vehicle's own page. Never exposes scanner identities. */
export async function getVehiclePassportStats(vehicleId: number): Promise<VehiclePassportStats> {
  const response = await apiService.makeRequest<VehiclePassportStatsApiShape>(
    `/passport_vehicle_stats.php?vehicle_id=${encodeURIComponent(String(vehicleId))}`,
    { method: 'GET' }
  );
  if (!response.success || !response.data) {
    throw new Error(response.message || response.error || 'Passport stats unavailable');
  }
  const raw = response.data;
  return {
    vehicle_id: vehicleId,
    public_count_enabled: raw.public_count_enabled,
    seen_in_person_count: raw.seen_in_person_strong ?? 0,
    qr_encounter_count: raw.qr_encounters_basic ?? 0,
  };
}

export async function listPassportCollections(
  filters: { collection_type?: string; owner_type?: string } = {}
): Promise<PassportCollectionDefinition[]> {
  const qs = new URLSearchParams();
  if (filters.collection_type) qs.set('collection_type', filters.collection_type);
  if (filters.owner_type) qs.set('owner_type', filters.owner_type);
  const suffix = qs.toString() ? `?${qs}` : '';
  const response = await apiService.makeRequest<PassportCollectionDefinition[]>(
    `/collections_list.php${suffix}`,
    { method: 'GET', headers: apiService.getAuthHeaders() }
  );
  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to load collections');
  }
  return Array.isArray(response.data) ? response.data : [];
}

/** Raw shape from `passportCollectionDetail()` — definition/requirements/progress are nested. */
interface PassportCollectionDetailApiShape {
  definition: PassportCollectionDefinition;
  version: { id: number; version_number: number; published_at: string } | null;
  requirements: PassportCollectionRequirement[];
  progress: { percent_complete: number; status: string; satisfied_requirements?: unknown[] } | null;
}

export async function getPassportCollectionDetail(
  slug: string,
  vehicleId?: number
): Promise<PassportCollectionDefinition> {
  const qs = new URLSearchParams({ slug });
  if (vehicleId) qs.set('vehicle_id', String(vehicleId));
  const response = await apiService.makeRequest<PassportCollectionDetailApiShape>(
    `/collection_detail.php?${qs.toString()}`,
    { method: 'GET', headers: apiService.getAuthHeaders() }
  );
  if (!response.success || !response.data) {
    throw new Error(response.message || response.error || 'Collection not found');
  }
  const raw = response.data;
  return {
    ...raw.definition,
    requirements: raw.requirements,
    progress: raw.progress ? { percent_complete: raw.progress.percent_complete, status: raw.progress.status } : null,
  };
}

export async function listPassportCampaigns(
  filters: { campaign_type?: string; owner_type?: string } = {}
): Promise<PassportCampaignSummary[]> {
  const qs = new URLSearchParams();
  if (filters.campaign_type) qs.set('campaign_type', filters.campaign_type);
  if (filters.owner_type) qs.set('owner_type', filters.owner_type);
  const suffix = qs.toString() ? `?${qs}` : '';
  const response = await apiService.makeRequest<PassportCampaignSummary[]>(
    `/campaigns_list.php${suffix}`,
    { method: 'GET', headers: apiService.getAuthHeaders() }
  );
  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to load campaigns');
  }
  return Array.isArray(response.data) ? response.data : [];
}

/** Raw shape from `passportCampaignDetail()` — campaign/objects/participant are nested. */
interface PassportCampaignDetailApiShape {
  campaign: PassportCampaignSummary;
  objects: PassportCampaignObject[];
  rewards?: unknown[];
  participant: { status: string; objects_visited_count: number; required_objects_total: number } | null;
}

export async function getPassportCampaignDetail(slug: string): Promise<PassportCampaignSummary> {
  const response = await apiService.makeRequest<PassportCampaignDetailApiShape>(
    `/campaign_detail.php?slug=${encodeURIComponent(slug)}`,
    { method: 'GET', headers: apiService.getAuthHeaders() }
  );
  if (!response.success || !response.data) {
    throw new Error(response.message || response.error || 'Campaign not found');
  }
  const raw = response.data;
  return {
    ...raw.campaign,
    objects: raw.objects,
    participation: raw.participant,
  };
}

export async function joinPassportCampaign(payload: {
  campaign_id: number;
  vehicle_id?: number;
  legal_accepted?: boolean;
  legal_terms_version?: string;
}): Promise<void> {
  const response = await apiService.makeRequest('/campaign_join.php', {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: apiService.getAuthHeaders(),
  });
  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to join campaign');
  }
}

// —— Admin moderation (super_admin only; admin/passport.php action-based facade) ——
// Contract: action=flags|set_flag|encounters|review|revoke_token|campaigns|
// campaign_save|campaign_object_add|collections|collection_save|
// collection_publish_version|appeal_resolve|health. See
// docs/BILT_PASSPORT_API_ARCHITECTURE.md §3.

const ADMIN_ENDPOINT = '/admin/passport.php';

function adminPassportUrl(action: string, params: Record<string, string | number | undefined> = {}): string {
  const qs = new URLSearchParams({ action });
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') qs.set(key, String(value));
  });
  return `${apiService.getAdminEndpoint(ADMIN_ENDPOINT)}?${qs}`;
}

async function adminPassportPost(action: string, body: Record<string, unknown> = {}): Promise<Record<string, unknown>> {
  const response = await apiService.makeRequest<Record<string, unknown>>(apiService.getAdminEndpoint(ADMIN_ENDPOINT), {
    method: 'POST',
    body: JSON.stringify({ action, ...body }),
    headers: apiService.getAuthHeaders(),
  });
  if (!response.success) {
    throw new Error(response.message || response.error || `Failed to complete admin action: ${action}`);
  }
  return response.data ?? {};
}

/** All 11 flags default false until explicitly turned on in `platform_settings`. */
export async function adminGetPassportFlags(): Promise<PassportFeatureFlags> {
  const response = await apiService.makeRequest<{ flags?: Record<string, boolean> }>(adminPassportUrl('flags'), {
    headers: apiService.getAuthHeaders(),
  });
  if (!response.success) {
    throw new Error(response.message || response.error || 'Failed to load Passport flags');
  }
  const flat = response.data?.flags || {};
  return { ...DISABLED_FLAGS, ...flat };
}

export async function adminSetPassportFlag(flag: keyof PassportFeatureFlags, enabled: boolean): Promise<void> {
  await adminPassportPost('set_flag', { flag, enabled });
}

/** Review queue = encounters filtered to `pending` (no dedicated queue table). */
export async function adminListPassportReviewQueue(status: string = 'pending'): Promise<PassportReviewQueueItem[]> {
  const response = await apiService.makeRequest<PassportReviewQueueItem[]>(
    adminPassportUrl('encounters', { status, limit: 50 }),
    { headers: apiService.getAuthHeaders() }
  );
  return Array.isArray(response.data) ? response.data : [];
}

export async function adminListPassportEncounters(filters: {
  subject_type?: string;
  status?: string;
  risk_status?: string;
} = {}): Promise<PassportEncounter[]> {
  const response = await apiService.makeRequest<PassportEncounter[]>(
    adminPassportUrl('encounters', { ...filters, limit: 100 }),
    { headers: apiService.getAuthHeaders() }
  );
  return Array.isArray(response.data) ? response.data : [];
}

export async function adminPassportReviewDecision(payload: {
  encounter_id: number;
  decision: 'approved' | 'rejected' | 'needs_more_info';
  reason?: string;
}): Promise<void> {
  await adminPassportPost('review', payload);
}

/**
 * There is no per-encounter "revoke" action on the backend today — only
 * `revoke_token` (invalidating the scan credential that produced encounters).
 * Until that ships, the closest available moderation lever is rejecting the
 * encounter, which is what this calls under the hood.
 */
export async function adminRevokePassportEncounter(encounterId: number, reason: string): Promise<void> {
  await adminPassportPost('review', { encounter_id: encounterId, decision: 'rejected', reason });
}

export async function adminRevokePassportScanToken(tokenId: number, reason: string): Promise<void> {
  await adminPassportPost('revoke_token', { token_id: tokenId, reason });
}

/**
 * No admin "list all appeals" action exists on the backend yet — appeals
 * currently surface only via `passport_scan_appeal.php` (the submitting
 * member's own list). This call will fail until that ships; callers should
 * treat the failure as an honest "not available yet" state.
 */
export async function adminListPassportAppeals(): Promise<PassportScanAppeal[]> {
  throw new Error('Appeal review queue is not available yet — no admin list endpoint exists.');
}

export async function adminResolvePassportAppeal(payload: {
  public_id: string;
  status: 'upheld_rejection' | 'overturned_approved' | 'withdrawn';
  resolution_notes?: string;
}): Promise<void> {
  await adminPassportPost('appeal_resolve', {
    appeal_public_id: payload.public_id,
    decision: payload.status,
    notes: payload.resolution_notes,
  });
}

export async function getPassportRecap(year?: number): Promise<Record<string, unknown>> {
  const qs = year ? `?year=${encodeURIComponent(String(year))}` : '';
  const response = await apiService.makeRequest<Record<string, unknown>>(`/passport_recap.php${qs}`, {
    method: 'GET',
    headers: apiService.getAuthHeaders(),
  });
  if (!response.success || !response.data) {
    throw new Error(response.message || response.error || 'Failed to load Passport recap');
  }
  return response.data;
}

export async function listPassportAchievements(): Promise<
  Array<{
    public_id: string;
    name: string;
    description?: string;
    template_key: string;
    short_label?: string;
    issued_at?: string;
    hidden?: boolean;
  }>
> {
  const response = await apiService.makeRequest<{ achievements?: unknown[] }>('/passport_achievements.php', {
    method: 'GET',
    headers: apiService.getAuthHeaders(),
  });
  const list = response.data?.achievements;
  return Array.isArray(list) ? (list as any[]) : [];
}

export async function getPassportOwnerAnalytics(vehicleId: number): Promise<Record<string, unknown>> {
  const response = await apiService.makeRequest<Record<string, unknown>>(
    `/passport_owner_analytics.php?vehicle_id=${encodeURIComponent(String(vehicleId))}`,
    { method: 'GET', headers: apiService.getAuthHeaders() }
  );
  if (!response.success || !response.data) {
    throw new Error(response.message || response.error || 'Failed to load owner analytics');
  }
  return response.data;
}

export async function adminGetPassportHealth(): Promise<Record<string, unknown>> {
  const response = await apiService.makeRequest<Record<string, unknown>>(adminPassportUrl('health'), {
    headers: apiService.getAuthHeaders(),
  });
  return (response.data as Record<string, unknown>) || {};
}
